import type {
  MatchReplaceOrigin,
  MatchReplaceRule,
} from '../store/proxy-store';
import type {
  BrowserMatchReplaceConditions,
  BrowserMatchReplaceRuleWithRelations,
} from '@terra/shared-schemas-orchestrator';
import {
  BrowserMatchLocationSchema,
  BrowserMatchTypeSchema,
  BrowserReplaceTypeSchema,
  MatchReplaceOriginSchema,
} from '@terra/shared-schemas-orchestrator';

const MT = BrowserMatchTypeSchema.enum;
const ML = BrowserMatchLocationSchema.enum;
const RT = BrowserReplaceTypeSchema.enum;

const matchTypeMap: Record<
  MatchReplaceRule['matchType'],
  (typeof MT)[keyof typeof MT]
> = {
  url: MT.URL,
  header: MT.HEADER,
  body: MT.BODY,
  statusCode: MT.STATUS_CODE,
  method: MT.METHOD,
  addHeader: MT.ADD_HEADER,
  removeHeader: MT.REMOVE_HEADER,
  paramName: MT.PARAM_NAME,
  paramValue: MT.PARAM_VALUE,
  firstLine: MT.FIRST_LINE,
};

const matchLocationMap: Record<
  MatchReplaceRule['matchLocation'],
  (typeof ML)[keyof typeof ML]
> = {
  request: ML.REQUEST,
  response: ML.RESPONSE,
  both: ML.BOTH,
};

const replaceTypeMap: Record<
  MatchReplaceRule['replaceType'],
  (typeof RT)[keyof typeof RT]
> = {
  string: RT.STRING,
  regex: RT.REGEX,
  function: RT.FUNCTION,
};

const OR = MatchReplaceOriginSchema.enum;

const originMap: Record<MatchReplaceOrigin, (typeof OR)[keyof typeof OR]> = {
  proxy: OR.PROXY,
  coder: OR.CODER,
  voyager: OR.VOYAGER,
};

const browserToLocalOrigin: Record<
  (typeof OR)[keyof typeof OR],
  MatchReplaceOrigin
> = {
  [OR.PROXY]: 'proxy',
  [OR.CODER]: 'coder',
  [OR.VOYAGER]: 'voyager',
};

function buildConditions(
  rule: MatchReplaceRule,
): BrowserMatchReplaceConditions | null {
  const c = rule.conditions;

  if (!c) {
    return null;
  }

  const hasAny =
    Boolean(c.urlPattern) ||
    (c.methods && c.methods.length > 0) ||
    (c.statusCodes && c.statusCodes.length > 0) ||
    (c.contentTypes && c.contentTypes.length > 0);

  if (!hasAny) {
    return null;
  }

  return {
    urlPattern: c.urlPattern ?? null,
    methods: c.methods ?? [],
    statusCodes: c.statusCodes ?? [],
    contentTypes: c.contentTypes ?? [],
  };
}

const browserToLocalMatchType: Record<
  (typeof MT)[keyof typeof MT],
  MatchReplaceRule['matchType']
> = {
  [MT.URL]: 'url',
  [MT.HEADER]: 'header',
  [MT.BODY]: 'body',
  [MT.STATUS_CODE]: 'statusCode',
  [MT.METHOD]: 'method',
  [MT.ADD_HEADER]: 'addHeader',
  [MT.REMOVE_HEADER]: 'removeHeader',
  [MT.PARAM_NAME]: 'paramName',
  [MT.PARAM_VALUE]: 'paramValue',
  [MT.FIRST_LINE]: 'firstLine',
};

const browserToLocalMatchLocation: Record<
  (typeof ML)[keyof typeof ML],
  MatchReplaceRule['matchLocation']
> = {
  [ML.REQUEST]: 'request',
  [ML.RESPONSE]: 'response',
  [ML.BOTH]: 'both',
};

const browserToLocalReplaceType: Record<
  (typeof RT)[keyof typeof RT],
  MatchReplaceRule['replaceType']
> = {
  [RT.STRING]: 'string',
  [RT.REGEX]: 'regex',
  [RT.FUNCTION]: 'function',
};

function parseConditionsFromBrowser(
  conditions: BrowserMatchReplaceConditions | null | undefined,
): MatchReplaceRule['conditions'] | undefined {
  if (!conditions) {
    return undefined;
  }

  const urlPattern =
    typeof conditions.urlPattern === 'string'
      ? conditions.urlPattern
      : undefined;
  const methods = Array.isArray(conditions.methods)
    ? conditions.methods.filter((m): m is string => typeof m === 'string')
    : [];
  const rawStatusCodes = Array.isArray(conditions.statusCodes)
    ? conditions.statusCodes
    : [];
  const statusCodes = rawStatusCodes
    .map((code) => (typeof code === 'number' ? code : Number(code)))
    .filter((code) => Number.isFinite(code));
  const contentTypes = Array.isArray(conditions.contentTypes)
    ? conditions.contentTypes.filter(
        (ct): ct is string => typeof ct === 'string',
      )
    : [];
  const hasAny =
    Boolean(urlPattern) ||
    methods.length > 0 ||
    statusCodes.length > 0 ||
    contentTypes.length > 0;

  if (!hasAny) {
    return undefined;
  }

  return {
    ...(urlPattern ? { urlPattern } : {}),
    ...(methods.length > 0 ? { methods } : {}),
    ...(statusCodes.length > 0 ? { statusCodes } : {}),
    ...(contentTypes.length > 0 ? { contentTypes } : {}),
  };
}

type StoredBrowserMatchReplaceRule = Omit<
  BrowserMatchReplaceRuleWithRelations,
  'applyTo'
> &
  Partial<Pick<BrowserMatchReplaceRuleWithRelations, 'applyTo'>>;

export function mapBrowserMatchReplaceRuleToLocal(
  rule: StoredBrowserMatchReplaceRule,
): MatchReplaceRule {
  const now = new Date();

  return {
    id: rule.id,
    name: rule.name,
    enabled: rule.enabled,
    description: rule.description ?? undefined,
    matchType: browserToLocalMatchType[rule.matchType],
    matchLocation: browserToLocalMatchLocation[rule.matchLocation],
    matchPattern: rule.matchPattern,
    isRegex: rule.isRegex,
    caseSensitive: rule.caseSensitive,
    headerName: rule.headerName ?? undefined,
    replaceWith: rule.replaceWith,
    replaceType: browserToLocalReplaceType[rule.replaceType],
    applyTo: (rule.applyTo ?? []).map((origin) => browserToLocalOrigin[origin]),
    conditions: parseConditionsFromBrowser(rule.conditions),
    createdAt: now,
    updatedAt: now,
  };
}

export function mapLocalMatchReplaceRuleToBrowser(
  rule: MatchReplaceRule,
): BrowserMatchReplaceRuleWithRelations {
  return {
    id: rule.id,
    name: rule.name,
    enabled: rule.enabled,
    description: rule.description ?? null,
    matchType: matchTypeMap[rule.matchType],
    matchLocation: matchLocationMap[rule.matchLocation],
    matchPattern: rule.matchPattern,
    isRegex: rule.isRegex,
    caseSensitive: rule.caseSensitive,
    headerName: rule.headerName ? rule.headerName : null,
    replaceWith: rule.replaceWith,
    replaceType: replaceTypeMap[rule.replaceType],
    applyTo: (rule.applyTo ?? []).map((origin) => originMap[origin]),
    conditions: buildConditions(rule),
  };
}
